import store, {StorePath} from './store.service';
import {isLocalhost} from './environment.service';
import {logger, loggerError, setCookie} from './utils.service';
import {OfferModel} from '../models/offer.model';
import {Lead} from '../models/lead.model';
import {Appointment} from '../models/appointment.model';
import {MOCK_ADDITIONAL_APPOINTMENTS} from '../mock/appointment.mock';
import {DEFAULT_ERROR_CHANNEL, ERRORS_TO_LOG_ONLY_ONCE} from '../models/errors.model';

const BASE_URL = isLocalhost() ? 'http://localhost/api/' : 'https://fullpath.com/api/';
const GPT_API_BASE_URL = `${BASE_URL}/lab/auto_gpt/`;


const defaultParams = (): URLSearchParams => {
	const params = new URLSearchParams();
	const storeData = store.getState()?.data as Record<string, any>;
	const trackData = storeData?.[StorePath.TRACK_DATA] as Record<string, any>;
	params.set('client', storeData?.[StorePath.TRACK_PARAMS]?.client);
	params.set('track', trackData?.track?.id);
	const userKey = trackData?.user_key;
	if (userKey) {
		params.set('user_key', userKey);
	}
	return params;
};

const markErrorAsLogged  = (errorKey: string): void => {
	if (ERRORS_TO_LOG_ONLY_ONCE.hasOwnProperty(errorKey)) {
		ERRORS_TO_LOG_ONLY_ONCE[errorKey] = true;
	}
};

export const initChat = (): Promise<Response> | void => {
	const path = 'users/me/chat/init';
	const params = defaultParams();
	if (params.get('client') !== 'undefined' && params.get('track') !== 'undefined') {
		return get(path, params);
	}
};

export const sendMessage = (message: string): Promise<Response> => {
	const path = 'users/me/chat/message';
	const params = defaultParams();
	params.set('text', message);
	return get(path, params);
};

export const expressInterestInOffer = (offer: OfferModel): Promise<Response> => {
	const path = `users/me/chat/offers/${offer.id}`;
	const params = defaultParams();
	params.set('offer_type', offer.type);
	return get(path, params);
};

export const expressInterestInCar = (carId: string): Promise<Response> => {
	const path = `users/me/chat/cars/${carId}`;
	const params = defaultParams();
	return get(path, params);
};

export const expressInterestInLocation = (location: string): Promise<Response> => {
	const path = `users/me/chat/location/${encodeURIComponent(location)}`;
	const params = defaultParams();
	return get(path, params);
};

export const leadConversion = (payload: Lead): Promise<Response> => {
	const path = `users/me/chat/conversion`;
	const params = defaultParams();
	params.set('payload', JSON.stringify(payload));
	return get(path, params);
};

export const bookAppointment = (payload: {lead: Lead, appointment: Appointment}): Promise<Response> => {
	const path = `users/me/chat/book_appointment`;
	if (isLocalhost()) {
		return new Promise((resolve) => {
			setTimeout(() => {
				resolve({ok: true, json: async () => ({id: '1234', is_test: true})} as Response);
			}, 2000);
		});
	}
	return post(path, payload);
};

export const getAvailableAppointments = (from: string): Promise<Response> => {
	const path = `users/me/chat/available_appointments`;
	if (isLocalhost()) {
		return new Promise((resolve) => {
			setTimeout(() => {
				resolve({ok: true, json: async () => ({appointments: MOCK_ADDITIONAL_APPOINTMENTS})} as Response);
			}, 2000);
		});
	}
	const params = defaultParams();
	params.set('from', from);
	return get(path, params);
};

const configureErrorString = (error: Error): string => {
	let errorStr = error.message;
	if (error.stack) {
		errorStr = `${errorStr}\n${error.stack}`;
	} else if (!error.message) {
		errorStr = JSON.stringify(error);
	}
	return errorStr;
};

export const logJsError = (error: Error, slackChannel = DEFAULT_ERROR_CHANNEL) => {
	const errorKey = error.message;
	if (ERRORS_TO_LOG_ONLY_ONCE[errorKey]) {
		return;
	}
	loggerError(error);
	const errorStr = configureErrorString(error);
	const path = `jserror`;
	const params = defaultParams();
	const trackParams = store.getState()?.data?.track_params as {[T: string]: string};
	params.set('url', trackParams.url);
	params.set('forty_n_t', trackParams.forty_n_t);
	params.set('browser', trackParams.browser);
	params.set('browser_ver', trackParams.browser_ver);
	params.set('identifier', 'Chat');
	params.set('error', errorStr);
	params.set('slack', slackChannel);

	get(path, params, false).then(
		(res) => {
			logger('error successfully logged', res);
		},
		(error) => {
			logger('error', error);
		}
	).finally(() => {
		markErrorAsLogged(errorKey);
    });
};

const logMissingUserKey = (params: URLSearchParams, path: string): void => {
	if (!params.get('user_key')) {
		const error = new Error('Chat-fe: API missing user_key param: ' + path);
		logJsError(error, '#js-we-logs');
	}
};

export const get = async (path: string, params: URLSearchParams, shouldLogMissingUserKey = true): Promise<Response> => {
	const url = new URL(`${BASE_URL}${path}`);
	const storeData = store.getState()?.data as Record<string, any>;
	setCookie('forty_n_user', storeData?.[StorePath.TRACK_PARAMS]?.user_key);
	url.search = params.toString();
	if (shouldLogMissingUserKey) {
		logMissingUserKey(params, path);
	}
	return await fetch(url, {credentials: 'include'});
};

export const post = async (path: string, data: Record<string, any>, shouldLogMissingUserKey = true): Promise<Response> => {
	const params = defaultParams();
	const url = new URL(`${BASE_URL}${path}`);
	const storeData = store.getState()?.data as Record<string, any>;
	setCookie('forty_n_user', storeData?.[StorePath.TRACK_PARAMS]?.user_key);
	url.search = params.toString();
	if (shouldLogMissingUserKey) {
		logMissingUserKey(params, path);
	}
	return await fetch(url, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		credentials: 'include',
		body: JSON.stringify(data),
	});
};

export const put = async <T>(path: string, data: object): Promise<T> => {
	const url = new URL(path, GPT_API_BASE_URL);
	const response = await fetch(url, {
		method: "PUT",
		body: JSON.stringify(data),
		headers: {
			"Content-Type": "application/json",
		},
	});
	return response.json() as Promise<T>;
};

export const remove = async <T>(path: string): Promise<T> => {
	const url = new URL(path, GPT_API_BASE_URL);
	const response = await fetch(url, {
		method: "DELETE",
	});
	return response.json() as Promise<T>;
};

export const handleApiResponse = (response: Response, returnResponsePromise = false) => {
	if (response.ok) {
		const contentType = response.headers.get('Content-Type');
		if (contentType?.includes('application/json')) {
			return returnResponsePromise ? response : response.json();
		} else {
			throw new Error('Unexpected response format: ' + contentType);
		}
	} else {
		response.text()
			.then((responseText) => {
				throw new Error(`Network response was not OK: ${responseText}`);
			})
			.catch((error) => {
				if (error?.message) {
					throw error;
				}
				throw new Error(`Network response was not OK. No Text`);
			});
	}
};
